import { apiClient } from '../utils/apiClient';
import { getSAProxyInvestorId, saProxyApiClient } from './saProxyAdapter';

/**
 * Smart Upload KYC document service.
 * Mirrors the /api/clients/me/kyc REST contract in KycSmartUploadController.
 * Plan of record: KYC_DOCUMENT_PLAN.md §3.4.
 */

export type KycRequirementState =
  | 'NOT_UPLOADED'
  | 'PENDING'
  | 'PENDING_REVIEW'
  | 'VALID'
  | 'DISCREPANCY'
  | 'EXPIRED'
  | 'EXPIRES_SOON'
  | 'OCR_FAILED';

export type KycDocumentTypeCode =
  | 'PAN_CARD'
  | 'PASSPORT'
  | 'AADHAR_CARD'
  | 'OCI_CARD'
  | 'ADDRESS_PROOF';

export type AddressProofType =
  | 'Utility Bill'
  | 'Rent Agreement'
  | 'Bank Statement'
  | 'Driving License'
  | 'Passport'
  | 'Aadhaar'
  | 'Other';

export interface KycRequirementSlotDto {
  documentType: KycDocumentTypeCode;
  requirement: 'MANDATORY' | 'CONDITIONAL' | 'OPTIONAL';
  state: KycRequirementState;
  currentDocumentId?: number | null;
  expiryDate?: string | null;
  daysUntilExpiry?: number | null;
  label: string;
  note?: string | null;
  /** VALID via SP approval of a legacy-path upload (no OCR) — badge shows "Approved". */
  viaSpApproval?: boolean;
  /** The approval belongs to another journey — badge shows "Accepted" instead. */
  reusedFromOtherJourney?: boolean;
}

export interface KycRequirementsResponseDto {
  investorType: string;
  usesAadhaarEsign: boolean;
  usesAadhaarForAddress: boolean;
  slots: KycRequirementSlotDto[];
  mandatoryTotal: number;
  mandatoryComplete: number;
  expiredCount: number;
  kycVerifiedAt?: string | null;
}

export interface KycDocumentFieldDto {
  fieldName: string;
  fieldValue?: string | null;
  confidence?: number | null;
  page?: number | null;
}

export interface KycDocumentDiscrepancyDto {
  id: number;
  canonicalSource: string;
  fieldName?: string | null;
  expectedValue?: string | null;
  observedValue?: string | null;
  severity: 'BLOCKING' | 'WARNING';
}

export interface KycSmartDocumentDto {
  id: number;
  investorUniqueId?: string;
  documentType: KycDocumentTypeCode;
  status?: string;
  validationStatus?: KycRequirementState;
  documentUrl?: string;
  originalFilename?: string;
  /** 1 = KYC identity doc (vault/tracker), 2 = journey onboarding doc — hidden from My Documents. */
  uploadType?: number;
  /** Journey stamp; null = shared vault doc (serves all of the person's journeys). */
  journeyId?: string | null;
  /** Person this document belongs to (family model); null = the login investor's person. */
  personId?: number | null;
  personName?: string | null;
  documentNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  addressProofType?: string | null;
  usesAadhaarForAddress?: boolean | null;
  ocrRunId?: string | null;
  ocrModelVersion?: string | null;
  ocrConfidence?: number | null;
  createdAt?: string;
  confirmedAt?: string | null;
  replacedByDocumentId?: number | null;
  fields: KycDocumentFieldDto[];
  discrepancies: KycDocumentDiscrepancyDto[];
  profileConflicts?: KycProfileConflictDto[];
}

export interface KycProfileConflictDto {
  targetField: string;
  existingValue?: string | null;
  documentValue?: string | null;
  reason?: string | null;
}

export interface UploadRequest {
  file: File;
  documentType: KycDocumentTypeCode;
  addressProofType?: AddressProofType;
  usesAadhaarForAddress?: boolean;
  /** Journey context (family redesign): scopes the doc to the journey's PERSON. */
  journeyId?: string | null;
}

class KycDocumentsService {
  private baseUrl = '/api/clients/me/kyc';

  // SA proxy mode (UAT 20, 2026-10-06): the agent's Smart Upload goes through the proxy
  // adapter to the agent's /smart-upload/* endpoints (a separate path on the API — the
  // agent side already has a legacy /kyc/documents). Investor mode is unchanged: raw
  // apiClient against /api/clients/me/kyc, exactly as before.
  private get client() {
    return getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;
  }

  private get base() {
    return getSAProxyInvestorId() !== null ? '/api/clients/me/smart-upload' : this.baseUrl;
  }

  async getRequirements(journeyId?: string | null): Promise<KycRequirementsResponseDto> {
    const { data } = await this.client.get<KycRequirementsResponseDto>(
      `${this.base}/requirements`,
      { params: journeyId ? { journeyId } : undefined }
    );
    return data;
  }

  async uploadDocument(req: UploadRequest): Promise<KycSmartDocumentDto> {
    const form = this.buildFormData(req);
    const { data } = await this.client.post<KycSmartDocumentDto>(
      `${this.base}/documents`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  }

  async reUpload(
    previousDocumentId: number,
    req: UploadRequest
  ): Promise<KycSmartDocumentDto> {
    const form = this.buildFormData(req);
    const { data } = await this.client.post<KycSmartDocumentDto>(
      `${this.base}/documents/${previousDocumentId}/re-upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  }

  async list(): Promise<KycSmartDocumentDto[]> {
    const { data } = await this.client.get<KycSmartDocumentDto[]>(
      `${this.base}/documents`
    );
    return data;
  }

  /** D1: document bytes for in-browser viewing (auth-carried fetch → object URL). */
  async viewInNewTab(id: number): Promise<void> {
    const res = await this.client.get(`${this.base}/documents/${id}/view`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    window.open(url, '_blank', 'noopener');
    // Give the new tab time to load the blob before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  /** H-15: latest OCR-imported value per profile field (locks matching Information inputs).
   *  SA proxy mode (2026-09-07): the only method in this service the agent's Information
   *  page calls — proxied so OCR-verified fields lock for agents too. Its SA route is
   *  /kyc/ocr-imported-values, hence baseUrl rather than the smart-upload base. */
  async ocrImportedValues(journeyId?: string | null): Promise<Record<string, string>> {
    const client = getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;
    const { data } = await client.get<Record<string, string>>(
      `${this.baseUrl}/ocr-imported-values`,
      { params: journeyId ? { journeyId } : undefined }
    );
    return data;
  }

  async detail(id: number): Promise<KycSmartDocumentDto> {
    const { data } = await this.client.get<KycSmartDocumentDto>(
      `${this.base}/documents/${id}`
    );
    return data;
  }

  async history(id: number): Promise<KycSmartDocumentDto[]> {
    const { data } = await this.client.get<KycSmartDocumentDto[]>(
      `${this.base}/documents/${id}/history`
    );
    return data;
  }

  async delete(id: number): Promise<void> {
    await this.client.delete(`${this.base}/documents/${id}`);
  }

  /**
   * Consent-at-accept (2026-08-25): pass journeyId when confirming inside a journey's
   * Smart Upload — the accept records that journey's KYC consent and VALID documents
   * archive to SharePoint/Dataverse immediately. Without journeyId (sidebar entry) the
   * confirm never creates consent.
   */
  async confirm(id: number, corrections?: Record<string, string>, journeyId?: string | null): Promise<KycSmartDocumentDto> {
    const { data } = await this.client.post<KycSmartDocumentDto>(
      `${this.base}/documents/${id}/confirm`,
      corrections && Object.keys(corrections).length ? corrections : undefined,
      { params: journeyId ? { journeyId } : undefined }
    );
    return data;
  }

  async reject(id: number): Promise<void> {
    await this.client.post(`${this.base}/documents/${id}/reject`);
  }

  private buildFormData(req: UploadRequest): FormData {
    const form = new FormData();
    form.append('file', req.file);
    form.append('documentType', req.documentType);
    if (req.addressProofType) form.append('addressProofType', req.addressProofType);
    if (typeof req.usesAadhaarForAddress === 'boolean') {
      form.append('usesAadhaarForAddress', String(req.usesAadhaarForAddress));
    }
    if (req.journeyId) form.append('journeyId', req.journeyId);
    return form;
  }
}

export const kycDocumentsService = new KycDocumentsService();
